import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  alpha,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchServicesMobile from "../search-services/SearchServicesMobile";
import { TypeAnimation } from "react-type-animation";
import { ResponseObject } from "@/app/classes/main";
import { ApiService } from "@/app/services/AppService";
import { SEARCH } from "../search-services/searchUi";

export default function SearchBarMobileView() {
  const [open, setOpen] = React.useState(false);
  const [isSearchItemSelected, setIsSearchItemSelected] = React.useState(false);
  const [searchServices, setSearchServices] = React.useState("");
  const [optionList, setOptionList] = useState<string[]>([]);
  const [serviceName, setServiceName] = useState("");
  const apiService = React.useMemo(() => new ApiService(), []);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
    setIsSearchItemSelected(false);
    setSearchServices("");
    setOptionList([]);
  };

  const handleSearch = (item: string) => {
    setServiceName(item);
    setSearchServices(item);
    setIsSearchItemSelected(true);
  };

  const handleClearSearch = () => {
    setSearchServices("");
    setOptionList([]);
  };

  const handleSearchInputServices = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchServices(e.target.value.toLowerCase());
  };

  const handleSearchFieldClick = () => {
    setIsSearchItemSelected(false);
  };

  const getSearchServices = React.useCallback(async () => {
    if (!searchServices?.trim()) {
      setOptionList([]);
      return;
    }
    try {
      const responseObject: ResponseObject | undefined =
        await apiService.get<ResponseObject>(
          apiService.searchServicesKeywords + searchServices
        );
      const response = responseObject?.object;
      setOptionList(Array.isArray(response) ? response : []);
    } catch {
      setOptionList([]);
    }
  }, [apiService, searchServices]);

  React.useEffect(() => {
    getSearchServices();
  }, [searchServices, getSearchServices]);

  const showSuggestions = searchServices.trim().length >= 1;
  const hasOptions = optionList.length > 0;

  const DrawerList = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        bgcolor: SEARCH.surface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.5,
          flexShrink: 0,
          background: `linear-gradient(180deg, #ffffff 0%, ${alpha(SEARCH.primary, 0.04)} 100%)`,
          borderBottom: `1px solid ${SEARCH.border}`,
        }}
      >
        <IconButton
          onClick={toggleDrawer(false)}
          aria-label="Close search"
          sx={{
            border: `1px solid ${SEARCH.border}`,
            bgcolor: "#fff",
            color: SEARCH.primaryDark,
            "&:hover": { bgcolor: alpha(SEARCH.primary, 0.06) },
          }}
        >
          <KeyboardBackspaceIcon sx={{ fontSize: 26 }} />
        </IconButton>
        <TextField
          autoFocus
          placeholder="Search facials, waxing, spa…"
          variant="outlined"
          fullWidth
          size="small"
          value={searchServices}
          onChange={handleSearchInputServices}
          onClick={handleSearchFieldClick}
          aria-label="Search services"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              bgcolor: "#fff",
              fontWeight: 600,
              "& fieldset": { borderColor: alpha(SEARCH.primary, 0.2) },
              "&:hover fieldset": { borderColor: alpha(SEARCH.primary, 0.35) },
              "&.Mui-focused fieldset": { borderColor: `${SEARCH.primary} !important` },
            },
            "& .MuiInputBase-input": {
              fontSize: "0.95rem",
              color: SEARCH.text,
            },
          }}
          InputProps={{
            endAdornment: searchServices ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small" aria-label="Clear search">
                  <CloseIcon sx={{ color: alpha(SEARCH.primary, 0.45), fontSize: 20 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 3,
          px: 1.5,
        }}
      >
        {isSearchItemSelected ? (
          <SearchServicesMobile serviceName={serviceName} />
        ) : (
          <Box sx={{ pt: 1.5 }}>
            {!showSuggestions ? (
              <Box sx={{ py: 4, textAlign: "center", px: 2 }}>
                <SearchIcon
                  sx={{ fontSize: 44, color: alpha(SEARCH.primary, 0.25), mb: 1.5 }}
                  aria-hidden
                />
                <Typography sx={{ fontWeight: 700, color: SEARCH.text, mb: 0.5 }}>
                  Find a service
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", color: SEARCH.muted, lineHeight: 1.5 }}>
                  Type to see suggestions, then pick one to view bookable services.
                </Typography>
              </Box>
            ) : hasOptions ? (
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: SEARCH.muted,
                    textTransform: "uppercase",
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  Suggestions
                </Typography>
                {optionList.map((item, index) => (
                  <Box
                    component="li"
                    key={`${item}-${index}`}
                    onClick={() => handleSearch(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      py: 1.35,
                      px: 1.25,
                      mb: 0.75,
                      borderRadius: "14px",
                      border: `1px solid ${SEARCH.border}`,
                      bgcolor: SEARCH.surfaceElevated,
                      cursor: "pointer",
                      transition: "background 0.15s ease, transform 0.12s ease",
                      "&:active": { transform: "scale(0.99)" },
                      "&:hover": {
                        bgcolor: alpha(SEARCH.primary, 0.06),
                        borderColor: alpha(SEARCH.primary, 0.22),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        bgcolor: alpha(SEARCH.primary, 0.1),
                        border: `1px solid ${alpha(SEARCH.primary, 0.15)}`,
                      }}
                    >
                      <SearchIcon sx={{ color: SEARCH.primary, fontSize: 20 }} />
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: SEARCH.text,
                        textAlign: "left",
                      }}
                    >
                      {item}
                    </Typography>
                    <ChevronRightIcon sx={{ color: alpha(SEARCH.primary, 0.35), fontSize: 22 }} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 5,
                  textAlign: "center",
                  px: 2,
                  borderRadius: SEARCH.radiusMd,
                  border: `1px dashed ${SEARCH.border}`,
                  bgcolor: alpha(SEARCH.primary, 0.03),
                }}
              >
                <Typography sx={{ fontWeight: 700, color: SEARCH.text, mb: 0.75 }}>
                  No matches yet
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", color: SEARCH.muted }}>
                  Try another spelling or a shorter keyword.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        component="button"
        type="button"
        onClick={toggleDrawer(true)}
        sx={{
          width: "100%",
          maxWidth: "100%",
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          textAlign: "left",
          WebkitTapHighlightColor: "transparent",
          display: "block",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            py: 1.125,
            px: { xs: 1.15, sm: 1.5 },
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            borderRadius: "16px",
            background: `linear-gradient(145deg, #ffffff 0%, ${alpha(SEARCH.primary, 0.04)} 52%, #f1f6f2 100%)`,
            border: `1px solid ${SEARCH.border}`,
            boxShadow: `0 2px 12px ${alpha(SEARCH.primary, 0.07)}, inset 0 1px 0 rgba(255,255,255,0.9)`,
            transition: "box-shadow 0.2s ease, transform 0.15s ease",
            "&:active": {
              transform: "scale(0.992)",
              boxShadow: `0 1px 8px ${alpha(SEARCH.primary, 0.1)}`,
            },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: `linear-gradient(145deg, ${alpha(SEARCH.primary, 0.14)} 0%, ${alpha(
                SEARCH.primary,
                0.08
              )} 100%)`,
              border: `1px solid ${alpha(SEARCH.primary, 0.2)}`,
            }}
          >
            <SearchIcon sx={{ color: SEARCH.primary, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 800,
                letterSpacing: "0.07em",
                color: alpha(SEARCH.primaryDark, 0.55),
                fontSize: "0.62rem",
                textTransform: "uppercase",
                mb: 0.35,
              }}
            >
              Search services
            </Typography>
            <Box
              component="div"
              sx={{
                fontSize: "0.92rem",
                fontWeight: 600,
                color: SEARCH.primaryDark,
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Box component="span" sx={{ color: SEARCH.mutedLight, fontWeight: 500 }}>
                Try{" "}
              </Box>
              <TypeAnimation
                sequence={[
                  "Body polishing",
                  1200,
                  "Facial",
                  1200,
                  "Waxing",
                  1200,
                  "Manicure",
                  1200,
                  "Pedicure",
                  1200,
                ]}
                wrapper="span"
                speed={40}
                style={{ display: "inline", fontWeight: 700 }}
                repeat={Infinity}
              />
            </Box>
          </Box>
          <ChevronRightIcon
            sx={{
              color: alpha(SEARCH.primary, 0.32),
              flexShrink: 0,
              fontSize: 22,
            }}
          />
        </Box>
      </Box>
      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        anchor="top"
        BackdropProps={{
          sx: { backgroundColor: "rgba(15, 36, 25, 0.48)" },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
            background: SEARCH.surface,
            borderRadius: 0,
            overflow: "hidden",
          },
        }}
        sx={{ zIndex: 1400 }}
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}
